import moment from "moment";
import React from "react";
import {useForm} from "react-hook-form";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {COUNTRY, MAX_MONTH_RANGE} from "../../constant";

import DateRangePicker from "./DateRangePicker";
import {faSearch, faUndo} from "@fortawesome/free-solid-svg-icons";

const DATE_FORMAT = "dd/MM/yyyy";
const SEARCH_DATE_FORMAT = "DD-MM-YYYY HH:mm:ss";

function SearchForm({ isLoading, search, clearBankCostRate }) {
  const {
    handleSubmit,
    getValues,
    control,
    clearErrors,
    setValue,
    register,
    formState: { errors },
  } = useForm({
    mode: "onChange",
  });

  function formatSearchDate(date, type = "START") {
    if (!date) return null;
    if (type === "START") {
      return moment(date).startOf("D").format(SEARCH_DATE_FORMAT);
    } else {
      return moment(date).endOf("D").format(SEARCH_DATE_FORMAT);
    }
  }

  function onSubmit(values) {
    const body = {
      transaction_date_start: formatSearchDate(
        values.transaction_date.from,
        "START"
      ),
      transaction_date_end: formatSearchDate(values.transaction_date.to, "END"),
      create_date_start: formatSearchDate(values.create_date.from, "START"),
      create_date_end: formatSearchDate(values.create_date.to, "END"),
      country_code: values.countryCode || null,
    };
    search(body);
  }

  function monthRangeIsMoreThanMaxMonthRange(from, to) {
    from = moment(from);
    to = moment(to);
    const monthDiff = to.diff(from, "months", true);
    return monthDiff > MAX_MONTH_RANGE;
  }

  function onChangeDate(from, to) {
    setValue("transaction_date", {
      from,
      to,
    });
    clearErrors([
      "transaction_date.from",
      "transaction_date.to",
      "create_date.from",
      "create_date.to",
    ]);
  }

  function onChangeUpdateDate(from, to) {
    setValue("create_date", {
      from,
      to,
    });
    clearErrors([
      "transaction_date.from",
      "transaction_date.to",
      "create_date.from",
      "create_date.to",
    ]);
  }

  function validateDateRange(from, to) {
    if (monthRangeIsMoreThanMaxMonthRange(from, to)) {
      return "Date range must not be more than " + MAX_MONTH_RANGE + " months";
    }
    return true;
  }

  function isRequired(value, fieldName, message) {
    let isRequired = true;
    const values = getValues();
    if (!values.transaction_date || !values.create_date) return;
    if (
      !values.transaction_date.from &&
      !values.transaction_date.to &&
      !values.create_date.from &&
      !values.create_date.to
    ) {
      isRequired = message;
    }
    switch (fieldName) {
      case "transaction_date.from":
        if (values.transaction_date.to && !value) isRequired = message;
        break;
      case "transaction_date.to":
        if (values.transaction_date.from && !value) isRequired = message;
        break;
      case "create_date.from":
        if (values.create_date.to && !value) isRequired = message;
        break;
      case "create_date.to":
        if (values.create_date.from && !value) isRequired = message;
        break;
      default:
        isRequired = true;
    }
    return isRequired;
  }

  function maxDate(date) {
    if (!date) return undefined;
    return moment(date).add("M", MAX_MONTH_RANGE).valueOf();
  }

  function minDate(date) {
    if (!date) return undefined;
    return moment(date).subtract("M", MAX_MONTH_RANGE).valueOf();
  }

  function handleClickClear() {
    setValue("transaction_date.from", undefined);
    setValue("transaction_date.to", undefined);
    setValue("create_date.from", undefined);
    setValue("create_date.to", undefined);
    setValue("countryCode", undefined);
    clearErrors();
    clearBankCostRate();
  }

  return (
    <form
      className="form"
      name="search-form"
      onSubmit={handleSubmit(onSubmit)}
      autoComplete="off"
      data-testid="search-form"
    >
      <div className="row">
        <div className="col-sm-4">
          <label>Transaction Date</label>
          <DateRangePicker
            control={control}
            prefixFieldName="transaction_date"
            startDate={getValues("transaction_date.from")}
            endDate={getValues("transaction_date.to")}
            minDate={minDate(getValues("transaction_date.to"))}
            maxDate={maxDate(getValues("transaction_date.from"))}
            dateFormat={DATE_FORMAT}
            onChange={onChangeDate}
            errors={errors}
            rules={{
              from: {
                validate: {
                  required: (v) =>
                    isRequired(
                      v,
                      "transaction_date.from",
                      "Please input date from"
                    ),
                  range: (v) =>
                    validateDateRange(v, getValues("transaction_date.to")),
                },
              },
              to: {
                validate: {
                  required: (v) =>
                    isRequired(
                      v,
                      "transaction_date.to",
                      "Please input date to"
                    ),
                },
              },
            }}
          />
        </div>
        <div className="col-sm-4">
          <label>Last Update Date</label>
          <DateRangePicker
            control={control}
            prefixFieldName="create_date"
            startDate={getValues("create_date.from")}
            endDate={getValues("create_date.to")}
            minDate={minDate(getValues("create_date.to"))}
            maxDate={maxDate(getValues("create_date.from"))}
            dateFormat={DATE_FORMAT}
            onChange={onChangeUpdateDate}
            errors={errors}
            rules={{
              from: {
                validate: {
                  required: (v) =>
                    isRequired(v, "create_date.from", "Please input date from"),
                  range: (v) =>
                    validateDateRange(v, getValues("create_date.to")),
                },
              },
              to: {
                validate: {
                  required: (v) =>
                    isRequired(v, "create_date.to", "Please input date to"),
                },
              },
            }}
          />
        </div>
        <div className="col-sm-4">
          <label>Country</label>
          <select
            className="form-control"
            {...register("countryCode")}
          >
            <option key="All" value={""}>
              Select All
            </option>
            {COUNTRY.map((x) => (
              <option key={x.code} value={x.code}>
                {x.name} ({x.currency})
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="row mt-4">
        <div className="col-sm-6 text-sm-right">
          <button
            type="submit"
            className="btn btn-warning"
            disabled={isLoading}
          >
            <FontAwesomeIcon icon={faSearch} /> Search
          </button>
        </div>
        <div className="col-sm-6">
          <button
            data-testid="clear-button"
            type="button"
            className="btn btn-secondary"
            disabled={isLoading}
            onClick={handleClickClear}
          >
            <FontAwesomeIcon icon={faUndo} /> Clear
          </button>
        </div>
      </div>
    </form>
  );
}

export default SearchForm;
